import { useEffect, useState, useRef, useMemo } from 'react'
import { useNls } from '../../nls/nlsProvider'
import { useWorkspace } from '../../context/useWorkspace'
import { getInitials, colorFor } from './avatarUtils'
import './workspaceSwitchCurtain.css'

interface CurtainState {
  visible: boolean
  targetWorkspaceId: string
  targetWorkspaceName: string
  targetAccentColor?: string
  startedAt: number
  isLeaving?: boolean
}

const MIN_CURTAIN_MS = 300
const MAX_CURTAIN_MS = 1500
const FADE_DURATION_MS = 200

/**
 * WorkspaceSwitchCurtain (PD2, S3, S3.fix1)
 * Fullscreen curtain covering workspace remounting on a switch.
 * - Listens for `workspace:switched`; ignores source 'SYSTEM' and initial load.
 * - Shows target workspace avatar color (branding.accentColor || colorFor), initials, and name.
 * - `role="status"`, `aria-live="polite"`, "Opening {name}…".
 * - Stays at least 300 ms; ends at first Timeline snapshot for target id or 1.5 s (measured to start of fade-out).
 * - Smooth entrance and exit animations; reduced motion uses opacity cross-fade only (no transform).
 */
export const WorkspaceSwitchCurtain = () => {
  const { t } = useNls()
  const { workspaces = [] } = useWorkspace()
  const workspacesRef = useRef(workspaces)
  useEffect(() => {
    workspacesRef.current = workspaces
  }, [workspaces])

  const [curtainState, setCurtainState] = useState<CurtainState | null>(null)
  const stateRef = useRef<CurtainState | null>(null)

  useEffect(() => {
    stateRef.current = curtainState
  }, [curtainState])

  const minTimerRef = useRef<number | null>(null)
  const maxTimerRef = useRef<number | null>(null)
  const fadeTimerRef = useRef<number | null>(null)

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false
    return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const startFadeOut = () => {
      if (minTimerRef.current !== null) {
        window.clearTimeout(minTimerRef.current)
        minTimerRef.current = null
      }
      if (maxTimerRef.current !== null) {
        window.clearTimeout(maxTimerRef.current)
        maxTimerRef.current = null
      }
      if (fadeTimerRef.current !== null) {
        window.clearTimeout(fadeTimerRef.current)
        fadeTimerRef.current = null
      }

      setCurtainState((prev) => (prev ? { ...prev, isLeaving: true } : null))
      if (stateRef.current) {
        stateRef.current = { ...stateRef.current, isLeaving: true }
      }

      fadeTimerRef.current = window.setTimeout(() => {
        setCurtainState(null)
        stateRef.current = null
        fadeTimerRef.current = null
      }, FADE_DURATION_MS)
    }

    const handleSwitch = (event: Event) => {
      const detail = (event as CustomEvent<{ from: string; to: string; source: string }>).detail
      if (!detail || detail.source === 'SYSTEM') return
      const targetId = detail.to
      if (!targetId) return

      const targetWs = workspacesRef.current.find((w) => w.id === targetId)
      const targetName = targetWs?.name || targetId
      const targetAccentColor = targetWs?.branding?.accentColor || colorFor(targetId)

      if (minTimerRef.current !== null) {
        window.clearTimeout(minTimerRef.current)
        minTimerRef.current = null
      }
      if (maxTimerRef.current !== null) {
        window.clearTimeout(maxTimerRef.current)
        maxTimerRef.current = null
      }
      if (fadeTimerRef.current !== null) {
        window.clearTimeout(fadeTimerRef.current)
        fadeTimerRef.current = null
      }

      const startedAt = Date.now()
      const nextState: CurtainState = {
        visible: true,
        targetWorkspaceId: targetId,
        targetWorkspaceName: targetName,
        targetAccentColor,
        startedAt,
        isLeaving: false,
      }

      stateRef.current = nextState
      setCurtainState(nextState)

      // Fallback: start fade-out at 1.5 s if no snapshot arrives
      maxTimerRef.current = window.setTimeout(() => {
        startFadeOut()
      }, MAX_CURTAIN_MS)
    }

    const handleTimelineFirstSnapshot = (event: Event) => {
      const current = stateRef.current
      if (!current || !current.visible || current.isLeaving) return

      const detail = (event as CustomEvent<{ workspaceId: string }>).detail
      if (detail?.workspaceId && detail.workspaceId !== current.targetWorkspaceId) {
        return
      }

      const elapsed = Date.now() - current.startedAt
      const remaining = Math.max(0, MIN_CURTAIN_MS - elapsed)

      if (minTimerRef.current !== null) {
        window.clearTimeout(minTimerRef.current)
      }

      minTimerRef.current = window.setTimeout(() => {
        startFadeOut()
      }, remaining)
    }

    window.addEventListener('workspace:switched', handleSwitch)
    window.addEventListener('timeline:first-snapshot', handleTimelineFirstSnapshot)

    return () => {
      window.removeEventListener('workspace:switched', handleSwitch)
      window.removeEventListener('timeline:first-snapshot', handleTimelineFirstSnapshot)
      if (minTimerRef.current !== null) {
        window.clearTimeout(minTimerRef.current)
        minTimerRef.current = null
      }
      if (maxTimerRef.current !== null) {
        window.clearTimeout(maxTimerRef.current)
        maxTimerRef.current = null
      }
      if (fadeTimerRef.current !== null) {
        window.clearTimeout(fadeTimerRef.current)
        fadeTimerRef.current = null
      }
    }
  }, []) // Empty deps: register listeners once, clear timers only on unmount

  if (!curtainState || !curtainState.visible) {
    return null
  }

  const { targetWorkspaceId, targetWorkspaceName, targetAccentColor } = curtainState
  const initials = getInitials(targetWorkspaceName)
  const avatarColor = targetAccentColor || colorFor(targetWorkspaceId)
  const openingText = t('NLSWorkspaceSwitchCurtainOpening', { name: targetWorkspaceName })

  const containerClasses = [
    'workspaceSwitchCurtain',
    prefersReducedMotion ? 'workspaceSwitchCurtain--reduced-motion' : '',
    curtainState.isLeaving ? 'workspaceSwitchCurtain--leaving workspaceSwitchCurtain--fading-out' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      role="status"
      aria-live="polite"
      className={containerClasses}
    >
      <div className="workspaceSwitchCurtain__card">
        <div
          className="workspaceSwitchCurtain__avatar"
          style={{ backgroundColor: avatarColor }}
          aria-hidden="true"
        >
          {initials}
        </div>
        <div className="workspaceSwitchCurtain__text">
          {openingText}
        </div>
      </div>
    </div>
  )
}
